import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useConfig } from '../../context/ConfigContext';

const defaultOwner = {
  id: 'owner-1',
  name: 'Account Owner',
  email: 'admin@deedstack.com',
  role: 'owner',
  status: 'active',
  lastLogin: new Date().toISOString(),
  createdAt: new Date().toISOString()
};

const roleDescriptions = [
  {
    role: 'Owner',
    description: 'Full access, can manage billing and users. Only one owner per account.',
    color: '#8b5cf6'
  },
  {
    role: 'Admin',
    description: 'Full access to properties, customers, loans, and reports. Cannot manage billing or users.',
    color: 'var(--forest-green)'
  },
  {
    role: 'Viewer',
    description: 'Read-only access to all data. Cannot edit, create, or delete anything.',
    color: 'var(--color-text-light)'
  }
];

const MAX_SEATS = 3;

function UserManagement() {
  const navigate = useNavigate();
  const { isDemoMode } = useConfig();
  const demoDisabled = isDemoMode();
  const demoStyle = demoDisabled ? { opacity: 0.5, cursor: 'not-allowed' } : {};
  const [users, setUsers] = useState([]);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('admin');
  const [inviteError, setInviteError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    // Load users from localStorage or use default
    const saved = localStorage.getItem('deedstack_admin_users');
    if (saved) {
      setUsers(JSON.parse(saved));
    } else {
      setUsers([defaultOwner]);
      localStorage.setItem('deedstack_admin_users', JSON.stringify([defaultOwner]));
    }
  }, []);

  const saveUsers = (updatedUsers) => {
    setUsers(updatedUsers);
    localStorage.setItem('deedstack_admin_users', JSON.stringify(updatedUsers));
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'active':
        return <span className="user-status-badge user-status-active">Active</span>;
      case 'invited':
        return <span className="user-status-badge user-status-invited">Invited</span>;
      case 'disabled':
        return <span className="user-status-badge user-status-disabled">Disabled</span>;
      default:
        return null;
    }
  };

  const getRoleBadge = (role) => {
    switch (role) {
      case 'owner':
        return <span className="user-role-badge user-role-owner">Owner</span>;
      case 'admin':
        return <span className="user-role-badge user-role-admin">Admin</span>;
      case 'viewer':
        return <span className="user-role-badge user-role-viewer">Viewer</span>;
      default:
        return null;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  const handleInviteUser = () => {
    setInviteError('');

    // Validate email
    if (!inviteEmail.trim()) {
      setInviteError('Please enter an email address');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(inviteEmail.trim())) {
      setInviteError('Please enter a valid email address');
      return;
    }

    // Check if email already exists
    if (users.some(u => u.email.toLowerCase() === inviteEmail.trim().toLowerCase())) {
      setInviteError('A user with this email already exists');
      return;
    }

    // Check seat limit
    const activeUsers = users.filter(u => u.status !== 'disabled');
    if (activeUsers.length >= MAX_SEATS) {
      setInviteError(`You've reached the maximum of ${MAX_SEATS} users. Upgrade to Enterprise for unlimited users.`);
      return;
    }

    // Create new user
    const newUser = {
      id: `user-${Date.now()}`,
      name: inviteEmail.split('@')[0],
      email: inviteEmail.trim().toLowerCase(),
      role: inviteRole,
      status: 'invited',
      lastLogin: null,
      createdAt: new Date().toISOString()
    };

    saveUsers([...users, newUser]);
    setShowInviteModal(false);
    setInviteEmail('');
    setInviteRole('admin');
    setSuccessMessage(`Invitation sent to ${newUser.email}`);
    setTimeout(() => setSuccessMessage(''), 4000);
  };

  const handleEditUser = (user) => {
    setEditingUser({ ...user });
    setShowEditModal(true);
  };

  const handleSaveEdit = () => {
    if (!editingUser) return;

    const updatedUsers = users.map(u =>
      u.id === editingUser.id ? editingUser : u
    );
    saveUsers(updatedUsers);
    setShowEditModal(false);
    setEditingUser(null);
    setSuccessMessage('User updated successfully');
    setTimeout(() => setSuccessMessage(''), 3000);
  };

  const handleToggleStatus = (user) => {
    if (user.role === 'owner') return;

    const newStatus = user.status === 'disabled' ? 'active' : 'disabled';
    const updatedUsers = users.map(u =>
      u.id === user.id ? { ...u, status: newStatus } : u
    );
    saveUsers(updatedUsers);
    setSuccessMessage(`User ${newStatus === 'disabled' ? 'disabled' : 'enabled'} successfully`);
    setTimeout(() => setSuccessMessage(''), 3000);
  };

  const handleRemoveUser = (user) => {
    if (user.role === 'owner') return;

    if (window.confirm(`Are you sure you want to remove ${user.email}? This action cannot be undone.`)) {
      const updatedUsers = users.filter(u => u.id !== user.id);
      saveUsers(updatedUsers);
      setSuccessMessage('User removed successfully');
      setTimeout(() => setSuccessMessage(''), 3000);
    }
  };

  const handleResendInvite = (user) => {
    setSuccessMessage(`Invitation resent to ${user.email}`);
    setTimeout(() => setSuccessMessage(''), 3000);
  };

  const owner = users.find(u => u.role === 'owner');
  const teamMembers = users.filter(u => u.role !== 'owner');
  const activeSeats = users.filter(u => u.status !== 'disabled').length;
  const seatPercentage = (activeSeats / MAX_SEATS) * 100;

  return (
    <div className="admin-dashboard-container">
      {/* Header */}
      <div className="admin-hub-header">
        <button
          onClick={() => navigate('/admin/settings-hub')}
          className="admin-hub-back-btn"
        >
          <span className="admin-hub-back-arrow">←</span>
          Back to Settings
        </button>
        <div className="user-mgmt-header-row">
          <div>
            <h1 className="admin-page-title">Team Members</h1>
            <p className="admin-hub-subtitle">Manage admin users and permissions</p>
          </div>
          <span className="user-mgmt-pro-badge">PRO Feature - Up to 3 admin users</span>
        </div>
      </div>

      {successMessage && (
        <div className="user-mgmt-success-message">{successMessage}</div>
      )}

      {/* Seat Limit Indicator */}
      <div className="card user-mgmt-seats-card">
        <div className="user-mgmt-seats-header">
          <h3>Team Seats</h3>
          <span className="user-mgmt-seats-count">{activeSeats}/{MAX_SEATS} seats used</span>
        </div>
        <div className="user-mgmt-seats-bar">
          <div
            className="user-mgmt-seats-fill"
            style={{
              width: `${seatPercentage}%`,
              backgroundColor: seatPercentage >= 100 ? 'var(--color-error)' : 'var(--forest-green)'
            }}
          />
        </div>
        <p className="user-mgmt-seats-upgrade">
          Need more seats? <a href="#upgrade">Upgrade to Enterprise</a> for unlimited users
        </p>
      </div>

      {/* Current Owner Section */}
      {owner && (
        <div className="card user-mgmt-owner-card">
          <div className="user-mgmt-owner-header">
            <h3>Account Owner</h3>
            {getRoleBadge('owner')}
          </div>
          <div className="user-mgmt-owner-details">
            <div className="user-mgmt-owner-avatar">
              {owner.name.charAt(0).toUpperCase()}
            </div>
            <div className="user-mgmt-owner-info">
              <div className="user-mgmt-owner-name">{owner.name}</div>
              <div className="user-mgmt-owner-email">{owner.email}</div>
              <div className="user-mgmt-owner-meta">
                Last login: {formatDate(owner.lastLogin)}
              </div>
            </div>
          </div>
          <p className="user-mgmt-owner-note">
            The account owner has full access and cannot be removed.
          </p>
        </div>
      )}

      {/* Team Members Section */}
      <div className="card user-mgmt-team-card">
        <div className="user-mgmt-team-header">
          <h3>Team Members</h3>
          <button
            className="btn btn-primary"
            onClick={() => !demoDisabled && setShowInviteModal(true)}
            disabled={activeSeats >= MAX_SEATS || demoDisabled}
            style={demoDisabled ? demoStyle : {}}
            title={demoDisabled ? 'Disabled in demo mode' : ''}
          >
            + Invite Team Member
          </button>
        </div>

        {teamMembers.length === 0 ? (
          <div className="user-mgmt-empty">
            <div className="user-mgmt-empty-icon">👥</div>
            <p>No team members yet</p>
            <p className="user-mgmt-empty-hint">Invite your team to help manage properties, customers, and loans</p>
          </div>
        ) : (
          <>
            {/* Desktop Table */}
            <div className="user-mgmt-table-container">
              <table className="user-mgmt-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Role</th>
                    <th>Last Login</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {teamMembers.map(user => (
                    <tr key={user.id} className={user.status === 'disabled' ? 'user-row-disabled' : ''}>
                      <td className="user-mgmt-table-name">{user.name}</td>
                      <td className="user-mgmt-table-email">{user.email}</td>
                      <td>{getRoleBadge(user.role)}</td>
                      <td className="user-mgmt-table-date">
                        {user.status === 'invited' ? '—' : formatDate(user.lastLogin)}
                      </td>
                      <td>{getStatusBadge(user.status)}</td>
                      <td className="user-mgmt-table-actions">
                        {user.status === 'invited' && (
                          <button
                            className="btn btn-small btn-secondary"
                            onClick={() => !demoDisabled && handleResendInvite(user)}
                            disabled={demoDisabled}
                            style={demoStyle}
                            title={demoDisabled ? 'Disabled in demo mode' : ''}
                          >
                            Resend
                          </button>
                        )}
                        <button
                          className="btn btn-small btn-secondary"
                          onClick={() => !demoDisabled && handleEditUser(user)}
                          disabled={demoDisabled}
                          style={demoStyle}
                          title={demoDisabled ? 'Disabled in demo mode' : ''}
                        >
                          Edit
                        </button>
                        <button
                          className={`btn btn-small ${user.status === 'disabled' ? 'btn-primary' : 'btn-secondary'}`}
                          onClick={() => !demoDisabled && handleToggleStatus(user)}
                          disabled={demoDisabled}
                          style={demoStyle}
                          title={demoDisabled ? 'Disabled in demo mode' : ''}
                        >
                          {user.status === 'disabled' ? 'Enable' : 'Disable'}
                        </button>
                        <button
                          className="btn btn-small btn-danger"
                          onClick={() => !demoDisabled && handleRemoveUser(user)}
                          disabled={demoDisabled}
                          style={demoStyle}
                          title={demoDisabled ? 'Disabled in demo mode' : ''}
                        >
                          Remove
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile Cards */}
            <div className="user-mgmt-mobile-cards">
              {teamMembers.map(user => (
                <div key={user.id} className={`user-mgmt-mobile-card ${user.status === 'disabled' ? 'user-card-disabled' : ''}`}>
                  <div className="user-mgmt-mobile-header">
                    <div className="user-mgmt-mobile-avatar">
                      {user.name.charAt(0).toUpperCase()}
                    </div>
                    <div className="user-mgmt-mobile-info">
                      <div className="user-mgmt-mobile-name">{user.name}</div>
                      <div className="user-mgmt-mobile-email">{user.email}</div>
                    </div>
                    <div className="user-mgmt-mobile-badges">
                      {getRoleBadge(user.role)}
                      {getStatusBadge(user.status)}
                    </div>
                  </div>
                  <div className="user-mgmt-mobile-meta">
                    Last login: {user.status === 'invited' ? 'Not yet' : formatDate(user.lastLogin)}
                  </div>
                  <div className="user-mgmt-mobile-actions">
                    {user.status === 'invited' && (
                      <button
                        className="btn btn-small btn-secondary"
                        onClick={() => !demoDisabled && handleResendInvite(user)}
                        disabled={demoDisabled}
                        style={demoStyle}
                        title={demoDisabled ? 'Disabled in demo mode' : ''}
                      >
                        Resend Invite
                      </button>
                    )}
                    <button
                      className="btn btn-small btn-secondary"
                      onClick={() => !demoDisabled && handleEditUser(user)}
                      disabled={demoDisabled}
                      style={demoStyle}
                      title={demoDisabled ? 'Disabled in demo mode' : ''}
                    >
                      Edit Role
                    </button>
                    <button
                      className={`btn btn-small ${user.status === 'disabled' ? 'btn-primary' : 'btn-secondary'}`}
                      onClick={() => !demoDisabled && handleToggleStatus(user)}
                      disabled={demoDisabled}
                      style={demoStyle}
                      title={demoDisabled ? 'Disabled in demo mode' : ''}
                    >
                      {user.status === 'disabled' ? 'Enable' : 'Disable'}
                    </button>
                    <button
                      className="btn btn-small btn-danger"
                      onClick={() => !demoDisabled && handleRemoveUser(user)}
                      disabled={demoDisabled}
                      style={demoStyle}
                      title={demoDisabled ? 'Disabled in demo mode' : ''}
                    >
                      Remove
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {/* Role Descriptions */}
      <div className="card user-mgmt-roles-card">
        <h3>Role Permissions</h3>
        <div className="user-mgmt-roles-grid">
          {roleDescriptions.map(item => (
            <div key={item.role} className="user-mgmt-role-item">
              <div className="user-mgmt-role-header">
                <span
                  className="user-mgmt-role-dot"
                  style={{ backgroundColor: item.color }}
                />
                <span className="user-mgmt-role-name">{item.role}</span>
              </div>
              <p className="user-mgmt-role-desc">{item.description}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Invite Modal */}
      {showInviteModal && (
        <div className="user-mgmt-modal-overlay" onClick={() => setShowInviteModal(false)}>
          <div className="user-mgmt-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-labelledby="invite-modal-title">
            <div className="user-mgmt-modal-header">
              <h3 id="invite-modal-title">Invite Team Member</h3>
              <button
                className="user-mgmt-modal-close modal-close-btn"
                onClick={() => setShowInviteModal(false)}
                aria-label="Close modal"
              >
                ×
              </button>
            </div>
            <div className="user-mgmt-modal-body">
              <div className="user-mgmt-modal-field">
                <label htmlFor="invite-email">Email Address</label>
                <input
                  type="email"
                  id="invite-email"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  placeholder="teammate@company.com"
                  className="user-mgmt-modal-input"
                  autoFocus
                />
              </div>
              <div className="user-mgmt-modal-field">
                <label htmlFor="invite-role">Role</label>
                <select
                  id="invite-role"
                  value={inviteRole}
                  onChange={(e) => setInviteRole(e.target.value)}
                  className="user-mgmt-modal-select"
                >
                  <option value="admin">Admin - Full access to manage data</option>
                  <option value="viewer">Viewer - Read-only access</option>
                </select>
              </div>
              {inviteError && (
                <div className="user-mgmt-modal-error">{inviteError}</div>
              )}
              <p className="user-mgmt-modal-note">
                User will receive an email invitation to join your team.
              </p>
            </div>
            <div className="user-mgmt-modal-footer">
              <button
                className="btn btn-secondary"
                onClick={() => setShowInviteModal(false)}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={() => !demoDisabled && handleInviteUser()}
                disabled={demoDisabled}
                style={demoStyle}
                title={demoDisabled ? 'Disabled in demo mode' : ''}
              >
                Send Invite
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && editingUser && (
        <div className="user-mgmt-modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="user-mgmt-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-labelledby="edit-modal-title">
            <div className="user-mgmt-modal-header">
              <h3 id="edit-modal-title">Edit Team Member</h3>
              <button
                className="user-mgmt-modal-close modal-close-btn"
                onClick={() => setShowEditModal(false)}
                aria-label="Close modal"
              >
                ×
              </button>
            </div>
            <div className="user-mgmt-modal-body">
              <div className="user-mgmt-modal-field">
                <label htmlFor="edit-name">Name</label>
                <input
                  type="text"
                  id="edit-name"
                  value={editingUser.name}
                  onChange={(e) => setEditingUser({ ...editingUser, name: e.target.value })}
                  className="user-mgmt-modal-input"
                  autoFocus
                />
              </div>
              <div className="user-mgmt-modal-field">
                <label>Email</label>
                <input
                  type="email"
                  value={editingUser.email}
                  disabled
                  className="user-mgmt-modal-input user-mgmt-modal-input-disabled"
                />
                <span className="user-mgmt-modal-hint">Email cannot be changed</span>
              </div>
              <div className="user-mgmt-modal-field">
                <label htmlFor="edit-role">Role</label>
                <select
                  id="edit-role"
                  value={editingUser.role}
                  onChange={(e) => setEditingUser({ ...editingUser, role: e.target.value })}
                  className="user-mgmt-modal-select"
                >
                  <option value="admin">Admin - Full access to manage data</option>
                  <option value="viewer">Viewer - Read-only access</option>
                </select>
              </div>
            </div>
            <div className="user-mgmt-modal-footer">
              <button
                className="btn btn-secondary"
                onClick={() => setShowEditModal(false)}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={() => !demoDisabled && handleSaveEdit()}
                disabled={demoDisabled}
                style={demoStyle}
                title={demoDisabled ? 'Disabled in demo mode' : ''}
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default UserManagement;
